import AttributeParser from '../utils/AttributeParser';
import { getSharedStyleSheet } from './sharedStyleSheet.js';

class FileBrowserChildContainer extends HTMLElement {
    static observedAttributes = ['id', 'department-id', 'project-id', 'path'];
    childItems = []; //[{id: 1, type: 'folder', path: '/test', title: 'test'},{id: 2, type: 'dept', path: '/', title: 'Dept'},{id: 3, type: 'file', path: '/', title: 'file'}];

    constructor() {
        super();
        this.childItems = [];
        this.browserRoot = null;
        this.moreItemsAvailable = false;
        this.nextIndex = 0;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open', delegatesFocus: true});

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'file-browser-child-container';
            this._wrapper.tabIndex = 0;
            this.shadowRoot.appendChild(this._wrapper);

            const spinner = `<div class="spinner"></div>`;
            this._wrapper.insertAdjacentHTML('beforeend', spinner);

            this.setupEvents();
        }

        this.dynamicRender();
    }

    setupEvents() {

    }

    dynamicRender() {
        if (!this._wrapper) return;
    }

    async populateChildren(project_id, department_id, path, start = 0) {
        try {
            this.moreItemsAvailable = false;

            let url = `/ajax/file_browse.php?project=${project_id}&dept_id=${department_id}&path=` + encodeURIComponent(path) + `&start=${start}`;
            if (AttributeParser.parseBooleanAttribute(this.browserRoot.getAttribute('hide-files'))) url += '&hide_files=1';
            if (this.browserRoot.getAttribute('file-types')) url += '&file_types=' + this.browserRoot.getAttribute('file-types');
            if (this.browserRoot.getAttribute('file-exts')) url += '&file_exts=' + this.browserRoot.getAttribute('file-exts');

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const json = await response.json();

            if (json.error) {
                throw new Error('The files could not be loaded.');
            } else {
                if (json.length >= 21) {
                    json.pop();
                    this.moreItemsAvailable = true;
                    this.nextIndex += 20;
                }

                this.childItems = this.childItems.concat(json);
                this.renderChildren();

                const item = this.browserRoot.getSelectedItem();
                if (item) this.browserRoot.updateSelection(item.id ?? -1, item.department_id ?? -1, item.path ?? '/');
            }

        } catch (error) {
            console.error(error.message);
            this._wrapper.replaceChildren();
            const message = document.createElement('p');
            message.setAttribute('role', 'alert');
            message.textContent = 'The files could not be loaded.';
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.textContent = 'Try again';
            retry.addEventListener('click', () => this.populateChildren(project_id, department_id, path, start));
            this._wrapper.append(message, retry);
        }
    }

    render() {
        if (this.childItems.length > 0) {
            this.renderChildren();
        } else {
            this.populateChildren(this.getAttribute('project-id'), this.getAttribute('department-id'), this.getAttribute('path'), 0);
        }
    }

    renderChildren() {
        this._wrapper.innerHTML = '';

        this.childItems.forEach((obj) => {
            if (obj.type === 'folder') {
                this.renderFolder(obj, AttributeParser.parseBooleanAttribute(this.browserRoot.getAttribute('can-select-folders') ?? true));
            } else if (obj.type === 'department') {
                this.renderDepartment(obj, AttributeParser.parseBooleanAttribute(this.browserRoot.getAttribute('can-select-depts') ?? true));
            } else if (obj.type === 'file') {
                this.renderFile(obj, AttributeParser.parseBooleanAttribute(this.browserRoot.getAttribute('can-select-files') ?? true));
            }
        });

        if (this.moreItemsAvailable) {
            const div = `<button type="button" class="more-items">Load more files and folders</button>`;
            this._wrapper.insertAdjacentHTML('beforeend', div);

            this._moreItems = this._wrapper.querySelector('.more-items');
            this._moreItems.addEventListener('click', e => {
                this.populateChildren(this.getAttribute('project-id'),this.getAttribute('department-id') ?? -1, this.getAttribute('path') ?? '/', this.nextIndex);
            })
        }
    }

    renderFolder(obj, selectable) {
        const folder = document.createElement('file-browser-folder');
        folder.setAttribute('file-id', obj.id);
        folder.setAttribute('file-title', obj.title ?? '');
        folder.setAttribute('project-id', this.getAttribute('project-id') ?? -1);
        folder.setAttribute('path', obj.path ?? '/');
        folder.setAttribute('department-id', obj.department_id ?? -1);
        folder.setBrowserRoot(this.browserRoot);

        if (selectable) folder.setAttribute('selectable', 'true');

        this._wrapper.appendChild(folder);
    }

    renderDepartment(obj, selectable) {
        const folder = document.createElement('file-browser-folder');
        folder.setAttribute('is-department', 'true');
        folder.setAttribute('file-title', obj.title ?? '');
        folder.setAttribute('project-id', this.getAttribute('project-id') ?? -1);
        folder.setAttribute('path', obj.path ?? '/');
        folder.setAttribute('department-id', obj.department_id ?? -1);
        folder.setBrowserRoot(this.browserRoot);

        if (selectable) folder.setAttribute('selectable', 'true');

        this._wrapper.appendChild(folder);
    }

    renderFile(obj, selectable) {
        const file = document.createElement('file-browser-file');
        file.setAttribute('file-id', obj.id);
        file.setAttribute('file-title', obj.title ?? '');
        file.setAttribute('project-id', this.getAttribute('project-id') ?? -1);
        file.setAttribute('path', obj.path ?? '/');
        file.setAttribute('department-id', obj.department_id ?? -1);
        file.setAttribute('thumbnail', obj.thumbnail ?? '');
        file.setBrowserRoot(this.browserRoot);

        if (selectable) file.setAttribute('selectable', 'true');

        this._wrapper.appendChild(file);
    }

    updateSelection(id, dept_id, path) {
        const items = this._wrapper.querySelectorAll('file-browser-folder,file-browser-file');
        items.forEach((obj) => {
            obj.setSelected(false);
            if (AttributeParser.parseBooleanAttribute(obj.getAttribute('is-department')) && dept_id > 0 &&
                path === '/' && +obj.getAttribute('department-id') === dept_id) {
                obj.setSelected(true);
            } else if (id > 0 && +obj.getAttribute('file-id') === id) {
                obj.setSelected(true);
            } else if (id === -1 && dept_id === -1 +obj.getAttribute('department-id') === dept_id &&
                +obj.getAttribute('file-id') === -1 && obj.getAttribute('path')  === '/') {
                obj.setSelected(true);
            }

            if (obj.tagName.toLowerCase() === 'file-browser-folder') {
                obj.updateSelection(id, dept_id, path);
            }
        });
    }

    setBrowserRoot(root) {
        this.browserRoot = root;
    }

    itemWithID(id) {
        const children = this._wrapper.querySelectorAll('file-browser-folder,file-browser-file').entries();

        for (const array of children) {
            const obj = array[1];
            const item = obj.itemWithID(id);
            if (item) {
                return item;
            }
        }

        return null;
    }

    itemWithPath(path, dept_id) {
        const children = this._wrapper.querySelectorAll('file-browser-folder').entries();

        for (const array of children) {
            const obj = array[1];
            const item = obj.itemWithPath(path, dept_id);
            if (item) {
                return item;
            }
        }

        return null;
    }
}

customElements.define('file-browser-child-container', FileBrowserChildContainer);

